/**
 * Custom Toast Utility with Deduplication
 *
 * Wraps react-hot-toast to prevent duplicate messages from showing
 * Tracks active toasts and prevents showing the same message multiple times
 */

import toast from 'react-hot-toast';

// Track active toast messages to prevent duplicates
const activeToasts = new Map();

/**
 * Show a toast only if the same message isn't already displayed
 * @param {Function} toastFn - The toast function to call (toast, toast.success, toast.error, etc.)
 * @param {string} message - The message to display
 * @param {Object} options - Additional toast options
 * @returns {string} Toast ID
 */
function showUniqueToast(toastFn, message, options = {}) {
  // Check if this message is already showing
  if (activeToasts.has(message)) {
    return activeToasts.get(message);
  }

  // Show the toast
  const toastId = toastFn(message, {
    ...options,
    // When toast is dismissed, remove from active tracking
    onDismiss: (t) => {
      activeToasts.delete(message);
      options.onDismiss?.(t);
    },
  });

  // Track this toast
  activeToasts.set(message, toastId);

  return toastId;
}

/**
 * Custom toast object with deduplication
 */
export const customToast = {
  // Default toast
  show: (message, options) => showUniqueToast(toast, message, options),

  // Success toast
  success: (message, options) => showUniqueToast(toast.success, message, options),

  // Error toast
  error: (message, options) => showUniqueToast(toast.error, message, options),

  // Loading toast
  loading: (message, options) => showUniqueToast(toast.loading, message, options),

  // Promise toast (handles loading, success, error states)
  promise: toast.promise,

  // Dismiss a specific toast
  dismiss: (toastId) => {
    toast.dismiss(toastId);
    // Remove from tracking if it exists
    for (const [message, id] of activeToasts.entries()) {
      if (id === toastId) {
        activeToasts.delete(message);
        break;
      }
    }
  },

  // Dismiss all toasts
  dismissAll: () => {
    toast.dismiss();
    activeToasts.clear();
  },

  // Custom toast (full control)
  custom: toast.custom,
};

export default customToast;
